import { useCallback, useEffect, useRef, useState } from 'react';
import Avatar from '../../assets/svg/avatar.svg';
import daburLogo from '../../assets/svg/appIcon.svg';
import InfiniteScroll from 'react-infinite-scroll-component';
import Markdown from 'markdown-to-jsx';
import classNames from 'classnames';
import {
    submitFileForTranscription,
    getTranscriptionStatus,
    generateTranscriptionSummary,
    setChatSessionId,
    setPage,
    setQueryMessage,
    uploadFiles,

    getContentHistory,
} from '../../lib/redux/actions';
import { useDispatch, useSelector } from 'react-redux';
import SkeletonLoader from '../../lib/common/components/loader/SkeletonLoader';
import { CustomLink, preprocessText } from '../../lib/utils/helper';
import MeetingTranscriptFileCard from './MeetingTranscriptFileCard';
import { useLocation } from 'react-router-dom';
import CopyToClipboard from '../../lib/common/components/CopyToClipboard';

import RotatingTextLoader from "../../lib/common/components/loader/RotatingTextLoader";
import PropTypes from 'prop-types';
import { trackEvent, trackException } from '../../lib/utils/telemetryService';
import BotMarkdown from '../../lib/common/components/markdown/BotMarkdown';
import useLoader from '../../hooks/useLoader';
import WelcomeMeetingTranscriptInterface from './WelcomeMeetingTranscriptInterface';
import ImportFileModal from '../ui/ImportFileModal';
import toast from '../../lib/utils/toast';

import { ChevronDown, ChevronUp } from 'lucide-react';


function convertContentToMedia(content, id) {
    if (!content) return { media: [] };

    try {
        const parsed = JSON.parse(content);
        const fileUrl = parsed.blob_url || parsed.mediaUrl;

        if (!fileUrl) return { media: [] };

        return {
            media: [
                {
                    fileUrl,
                    fileName: parsed.filename || parsed.fileName,
                    _id: id || generateObjectId(),
                },
            ],
        };
    } catch (error) {
        console.error("Invalid JSON string:", error);
        return { media: [] };
    }
}

function generateObjectId() {
    return Math.random().toString(16).substring(2, 26);
}
export default function MeetingTranscriptChatInterface({
    isMobile,
    sidebarOpen,
    setChats,
    chats,
}) {
    const location = useLocation();
    const textRef = useRef('');
    const scrollRef = useRef(null);
    const isFetchingRef = useRef(false);

    const dispatch = useDispatch();
    const { selectedChat, page, sessionId, queryMessage } = useSelector(
        state => state.chat,
    );

    const [isImportFileOpen, setIsImportFileOpen] = useState(false);
    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    const textDelayTimeout = useRef(null);
    const [displayedText, setDisplayedText] = useState('');
    const [tempId, setTempId] = useState(null);
    const inputRef = useRef(null);
    const [inputText, setInputText] = useState('');
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const scrollHeightRef = useRef(0);
    const scrollTimeoutRef = useRef(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);


    const { hideLoader } = useLoader()
    const [expandedZipcodes, setExpandedZipcodes] = useState({});

    const toggleZipcode = (chatId, zipcodeKey, defaultOpen = false) => {
        setExpandedZipcodes(prev => {
            const current = prev[chatId] && prev[chatId][zipcodeKey];
            const next = current === undefined ? !defaultOpen : !current;
            return {
                ...prev,
                [chatId]: {
                    ...(prev[chatId] || {}),
                    [zipcodeKey]: next,
                },
            };
        });
    };

    useEffect(() => {

        handleGetWeatherForecastHistory(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGetWeatherForecastHistory = async (pageNumber = 1) => {
        if (isFetchingRef.current) return;

        try {
            isFetchingRef.current = true;
            setIsLoadingMore(true);
            if (pageNumber === 1) setIsLoadingHistory(true);

            // Store current scroll height before loading new data
            if (scrollRef.current) {
                scrollHeightRef.current = scrollRef.current.scrollHeight;
            }

            const data = {
                page: pageNumber,
                limit: 10,
                chatType: 10,
            };

            const res = await getContentHistory(data);
            const newChats = res?.data?.data || [];

            // Filter out duplicate entries (where questionId is null)
            const filteredChats = newChats.filter((chat) => chat.questionId !== null);

            const existingIds = new Set(chats.map((chat) => chat._id));
            const uniqueNewChats = filteredChats.filter(
                (chat) => !existingIds.has(chat._id)
            );



            const processedMessages = uniqueNewChats.flatMap((chat) => {
                let formattedContent = "";
                const result = convertContentToMedia(
                    chat?.questionId?.content,
                    chat?.questionId?._id,
                );

                if (!chat?.content) {
                    formattedContent = "";
                } else {
                    try {
                        const parsedContent = JSON.parse(chat.content);

                        if (parsedContent?.summary?.length) {
                            formattedContent = parsedContent.summary.join("\n\n");
                        } else {
                            formattedContent = chat.content;
                        }

                    } catch (error) {
                        formattedContent = chat.content; // if not JSON
                    }
                }

                return [
                    {
                        role: 'user',
                        message: 'Generate Transcript',
                        _id: chat?.questionId?._id,
                        type: 'text',
                        date: chat?.questionId?.createdAt || chat?.createdAt,
                        media: result.media.length > 0 ? result.media : []
                    },
                    {
                        role: 'assistant',
                        message: formattedContent,
                        _id: `bot-${chat._id}`,
                        date: chat?.createdAt,
                        summary: chat?.transcriber?.summary || "",
                    },
                ];
            });


            setChats(prev => [...processedMessages, ...prev]);

            setHasMore(processedMessages.length > 0);

            // After state update, adjust scroll position
            setTimeout(() => {
                if (scrollRef.current) {
                    const newScrollHeight = scrollRef.current.scrollHeight;
                    const prevScrollTop = scrollRef.current.scrollTop;
                    const scrollDiff = newScrollHeight - scrollHeightRef.current;
                    scrollRef.current.scrollTop = prevScrollTop + scrollDiff;
                }
            }, 0);
        } catch (error) {
            console.error(error);
            trackException(error);
        } finally {
            isFetchingRef.current = false;
            setIsLoadingMore(false);
            if (pageNumber === 1) setIsLoadingHistory(false);
        }
    };

    const fetchMoreData = async () => {
        if (isFetchingRef.current || !hasMore || isLoadingMore) return;

        // Only fetch more if we're near the top
        if (scrollRef.current && scrollRef.current.scrollTop > 100) return;

        // Clear any existing timeout
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        // Set a new timeout
        scrollTimeoutRef.current = setTimeout(async () => {
            const nextPage = page + 1;
            await handleGetWeatherForecastHistory(nextPage);
            setPage(nextPage);
        }, 300); // 300ms debounce
    };

    const handleTextDelay = useCallback(msg => {
        // Clear any existing timeouts
        if (textDelayTimeout.current) {
            clearTimeout(textDelayTimeout.current);
        }

        let currentIndex = 0;
        const typingSpeed = 2;
        textRef.current = ''; // Use ref instead of state for intermediate values

        const typeNextCharacter = () => {
            if (currentIndex < msg.length) {
                textRef.current = msg.substring(0, currentIndex + 1);
                setDisplayedText(textRef.current);
                currentIndex += 1;
                textDelayTimeout.current = setTimeout(typeNextCharacter, typingSpeed);
            }
        };

        typeNextCharacter();

        // Cleanup function
        return () => {
            if (textDelayTimeout.current) {
                clearTimeout(textDelayTimeout.current);
            }
        };
    }, []);



    useEffect(() => {
        if (
            loading &&
            queryMessage &&
            sessionId &&
            sessionId === selectedChat?._id
        ) {
            setChats(prev => [...prev, queryMessage]);
        }
        // eslint-disable-next-line
    }, [selectedChat]);

    useEffect(() => {
        if (
            location.pathname !== '/meeting-transcript' &&
            selectedChat?._id &&
            (chats?.length === 0 ||
                (chats[chats.length - 1]?._id &&
                    !chats[chats.length - 1]._id.includes('temp-user-')))
        ) {
            // Clear existing chats when selecting a new chat
            setChats([]);
            handleGetWeatherForecastHistory(1);
        }
        // eslint-disable-next-line
    }, [selectedChat]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.scrollLeft = inputRef.current.scrollWidth;
        }
    }, [inputText]);

    useEffect(() => {
        if (chats.length > 0) {
            const lastChat = chats[chats.length - 1];
            if (lastChat.role === 'assistant' && lastChat.latest_message) {
                handleTextDelay(lastChat.message);
            } else {
                // Immediately show full message if not latest assistant message
                setDisplayedText(lastChat.message);
            }
        }
    }, [chats, handleTextDelay]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && textDelayTimeout.current) {
                clearTimeout(textDelayTimeout.current);
            } else if (!document.hidden && chats.length > 0) {
                // When returning to the tab, immediately show the full text
                const lastChat = chats[chats.length - 1];
                if (lastChat.role === 'assistant' && lastChat.latest_message) {
                    setDisplayedText(lastChat.message);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (textDelayTimeout.current) {
                clearTimeout(textDelayTimeout.current);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [chats]);

    const handleFileUpload = async (selectedFiles) => {
        if (!selectedFiles || selectedFiles.length === 0) return;

        const allowedTypes = ["audio/mpeg", "audio/wav", "audio/mp4", "video/mp4", "audio/x-m4a"];
        const MAX_FILE_SIZE_MB = 2048; // 2GB


        // Only take the first file for single file upload
        const file = selectedFiles[0];

        // Validate the file
        const isAudioVideo = file.name.endsWith('.mp3') ||
            file.name.endsWith('.wav') ||
            file.name.endsWith('.mp4') ||
            file.name.endsWith('.m4a') ||
            allowedTypes.includes(file.type);

        const isValidSize = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

        if (!isAudioVideo) {
            toast.error(`Unsupported format: ${file.name}`);
            return;
        }

        if (!isValidSize) {
            toast.error(`File size is too big (Maximum allowed size is 2GB)`);
            return;
        }

        // const getDuration = (file) => new Promise((resolve) => {
        //     const url = URL.createObjectURL(file);
        //     const isVideo = file.type.startsWith('video/') || file.name.endsWith('.mp4');
        //     const media = document.createElement(isVideo ? 'video' : 'audio');
            
        //     media.onloadedmetadata = () => {
        //         URL.revokeObjectURL(url);
        //         resolve(media.duration);
        //     };
            
        //     media.onerror = () => {
        //         URL.revokeObjectURL(url);
        //         resolve(0); // If we can't read duration, allow it to pass or fallback to server validation
        //     };
            
        //     media.src = url;
        // });

        // setIsUploading(true);
        // const duration = await getDuration(file);
        // setIsUploading(false);

        // if (duration > 300 * 60) {
        //     toast.error(`File duration must not exceed 300 minutes`);
        //     return;
        // }

        const newFileEntry = {
            id: Date.now(),
            file,
            fileName: file.name,
            size: file.size,
            status: 'uploading'
        };

        setFiles([newFileEntry]);

        try {
            setIsUploading(true);
            const formData = new FormData();
            formData.append("files", file);

            trackEvent("MeetingTranscript_FileUpload_Start", { fileName: file.name });

            const result = await uploadFiles(formData);

            if (result.statusCode === 200) {
                trackEvent("MeetingTranscript_FileUpload_Success", { uploaded: result.data.length });

                setFiles([{
                    ...newFileEntry,
                    status: 'completed',
                    fileUrl: result.data[0],
                    previewUrl: URL.createObjectURL(file),
                }]);
            }
        } catch (error) {
            trackException(error);
            trackEvent("FileUpload_Failed");
            toast.error("File upload failed. Please try again.");
            console.error(error);
            setFiles([]);
        } finally {
            setIsUploading(false);
        }
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleChatResponse = async ({ payload } = {}) => {

        let tempSessionId = selectedChat?._id || isSelectedSessionId?.current?._id;
        setTempId(selectedChat?._id || isSelectedSessionId?.current?._id);

        // Attach media files if available
        const mediaAttachments = files.map(f => ({ fileUrl: f.fileUrl, fileName: f.fileName, previewUrl: f.previewUrl }));
        const textInput = "Generate Transcript";

        setInputText(textInput)

        const tempUserMessage = {
            role: 'user',
            message: textInput,
            media: mediaAttachments,
        };

        dispatch(setQueryMessage(tempUserMessage));
        dispatch(setChatSessionId(selectedChat?._id));

        setIsImportFileOpen(false); // Close modal if open

        setTimeout(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollTop = 0;
            }
        }, 100);

        try {
            setLoading(true);

            if (mediaAttachments.length === 0) {
                toast.error('Please upload at least one audio/video file');
                setLoading(false);
                hideLoader();
                return;
            }

            const userMessage = {
                role: 'user',
                message: textInput,
                date: Date.now(),
                media: mediaAttachments
            };

            setChats(prev => [...prev, userMessage]);
            trackEvent('MeetingTranscript_UserFileSubmitted', {
                message: textInput,
                sessionId: tempSessionId,
                fileCount: mediaAttachments.length,
            });

            // Step 1: Submit file for transcription
            const transcriptionPayload = {
                blob_url: mediaAttachments[0].fileUrl,
                filename: mediaAttachments[0].fileName
            };

            // Add loading message with progress bar
            const loadingMessage = {
                role: 'assistant',
                message: 'Transcribing your meeting...',
                _id: `loading-${Date.now()}`,
                type: 'loading',
                progress: 0,
                // jobId: data?.job_id,
                // chatId: data?.chatId,
            };
            setChats(prev => [...prev, loadingMessage]);

            const res = await submitFileForTranscription(transcriptionPayload);
            const { data } = res;

            if (data?.chatId) {
                setChats(prev => {
                    const updated = [...prev];
                    for (let i = updated.length - 1; i >= 0; i -= 1) {
                        if (updated[i].role === 'user' && !updated[i]._id) {
                            updated[i] = { ...updated[i], _id: data.chatId };
                            break;
                        }
                    }
                    return updated;
                });
            }

            // Clear files after sending
            setFiles([]);

            // Step 2: Poll for transcription status
            const pollInterval = setInterval(async () => {
                try {
                    const statusRes = await getTranscriptionStatus(data?.job_id);
                    const statusData = statusRes.data;

                    if (statusData?.isCompleted && statusData?.transcript) {
                        clearInterval(pollInterval);

                        // Update loading message with transcript
                        // const transcriptMessage = {
                        //     role: 'assistant',
                        //     message: statusData.transcript,
                        //     _id: `transcript-${Date.now()}`,
                        //     date: Date.now(),
                        //     jobId: data?.job_id,
                        //     fromCache: statusData?.fromCache || false,
                        // };

                        // setChats(prev => {
                        //     const updatedChats = prev.map(chat =>
                        //         chat._id === loadingMessage._id ? transcriptMessage : chat,
                        //     );
                        //     return updatedChats;
                        // });
                        // setDisplayedText(transcriptMessage.message);

                        trackEvent('MeetingTranscript_TranscriptionCompleted', {
                            jobId: data?.job_id,
                            fromCache: statusData?.fromCache,
                        });


                        // Step 3: Generate summary automatically
                        try {
                            const summaryRes = await generateTranscriptionSummary({ job_id: data?.job_id });
                            const summaryData = summaryRes.data;

                            // // Update the transcript message to include summary
                            // setChats(prev => prev.map(chat => {
                            //     if (chat._id === `transcript-${transcriptMessage._id.split('-')[1]}`) {
                            //         return {
                            //             ...chat,
                            //             summary: summaryData?.summary,
                            //             fromCache: summaryData?.fromCache || chat.fromCache
                            //         };
                            //     }
                            //     return chat;
                            // }));

                            // 🔹 Step 2: Create final assistant message (Transcript + Summary)
                            const finalAssistantMessage = {
                                role: 'assistant',
                                message: statusData.transcript,
                                summary: summaryData?.summary,
                                _id: `transcript-${Date.now()}`,
                                date: Date.now(),
                                jobId: data?.job_id,
                                fromCache: summaryData?.fromCache || statusData?.fromCache || false,
                            };

                            // 🔹 Step 3: Replace loading message ONLY ONCE
                            setChats(prev =>
                                prev.map(chat =>
                                    chat._id === loadingMessage._id
                                        ? finalAssistantMessage
                                        : chat
                                )
                            );

                            setDisplayedText(finalAssistantMessage.message);

                            trackEvent('MeetingTranscript_SummaryGenerated', {
                                jobId: data?.job_id,
                                fromCache: summaryData?.fromCache,
                            });
                        } catch (summaryError) {
                            console.error('Error generating summary:', summaryError);
                            trackException(summaryError);
                            toast.error('Failed to generate summary');
                        }

                        setLoading(false);
                    } else if (statusData?.status === 'failed' || statusData?.error_message) {
                        clearInterval(pollInterval);
                        const errorMessage = {
                            role: 'assistant',
                            message: statusData?.error_message || 'Transcription failed',
                            _id: `error-${Date.now()}`,
                            statusCode: 'error',
                        };
                        setChats(prev => {
                            const updatedChats = prev.map(chat =>
                                chat._id === loadingMessage._id ? errorMessage : chat,
                            );
                            return updatedChats;
                        });
                        setLoading(false);
                    }
                } catch (error) {
                    console.error('Error fetching transcription status:', error);
                    trackException(error);
                    clearInterval(pollInterval);
                    setLoading(false);
                }
            }, 15000); // Poll every 5 seconds
        } catch (error) {
            const errorBotMessage = {
                role: 'assistant',
                message: error?.message || 'Something went wrong!!',
                _id: `error-bot-${Date.now()}`,
                statusCode: 'error',
            };
            trackException(error);
            setChats(prev => {
                // Remove any existing loading message
                const withoutLoader = prev.filter(
                    chat => chat.type !== 'loading'
                );

                return [...withoutLoader, errorBotMessage];
            });
            setLoading(false);
            hideLoader()
        } finally {
            // setLoading(false);
        }
    };


    const isSelectedSessionId = useRef(selectedChat);

    useEffect(() => {
        isSelectedSessionId.current = selectedChat;
    }, [selectedChat]);





    return (
        <div
            className={`flex-1 flex flex-col overflow-auto sm:overflow-hidden  ${isMobile && sidebarOpen ? 'opacity-50' : ''
                }`}>

            <ImportFileModal
                open={isImportFileOpen}
                onClose={() => setIsImportFileOpen(false)}
                onGenerate={handleChatResponse}
                onFileUpload={handleFileUpload}
                onRemoveFile={removeFile}
                files={files}
                isUploading={isUploading}
            />

            {!isLoadingHistory && !selectedChat?._id && chats?.length === 0 && (
                <WelcomeMeetingTranscriptInterface
                    setIsImportFileOpen={setIsImportFileOpen}
                    setFiles={setFiles}
                />
            )}

            {isLoadingHistory ? (
                <div className="flex h-full items-center justify-center">
                    <SkeletonLoader />
                </div>
            ) : chats?.length > 0 ? (
                <div className="flex flex-col  overflow-auto">
                    <div
                        id="scrollableDiv"
                        ref={scrollRef}
                        className="flex-1 pt-4 !px-[30px] sm:px-[50px] md:px-[20px] lg:px-[100px] xl:!px-[220px] 2xl:!px-[220px] overflow-y-auto flex flex-col-reverse mt-[40px]">
                        <InfiniteScroll
                            inverse={true}
                            dataLength={chats.length}
                            next={fetchMoreData}
                            hasMore={hasMore}
                            scrollableTarget="scrollableDiv"
                            loader={<div className="mx-auto" key={0}></div>}
                            endMessage={
                                chats?.totalPages >= 2 ? (
                                    <p className="text-center m-2 font-segoeUI">No More chats!</p>
                                ) : (
                                    ''
                                )
                            }
                            scrollThreshold="200px"
                            className="overflow-hidden">
                            {chats.map((chat, i) =>
                                chat.role === 'user' ? (
                                    <div
                                        key={chat._id || `chat-${i}`}
                                        className={`flex group justify-end ${i === 0 ? 'mt-12 mb-8 ' : 'mt-8 mb-8'
                                            }`}>
                                        <div className="flex flex-row  gap-2">
                                            <div className="flex flex-col items-end">
                                                <div className="bg-[#1b5c2926] text-[#000000] flex flex-col gap-2 overflow-x-auto rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] p-[14px] min-h-[44px] justify-center w-fit group">
                                                    <div className="markDownWrapper !leadings-[27px] font-hel leading-[27px] w-fit whitespace-pre-line break-words font-medium text-sm ">
                                                        <Markdown
                                                            key={`chat-${chat?._id}`}
                                                            options={{
                                                                overrides: {
                                                                    a: { component: CustomLink },
                                                                },
                                                            }}>
                                                            {preprocessText(chat.message)}
                                                        </Markdown>
                                                    </div>
                                                    {chat.media && chat.media.length > 0 && (
                                                        <div className="mt-2 flex flex-wrap gap-2">
                                                            {chat.media.map((mediaItem, fileIndex) => (
                                                                <MeetingTranscriptFileCard
                                                                    index={i}
                                                                    key={`filecard-${mediaItem.fileUrl}`}
                                                                    mediaItem={mediaItem}
                                                                    fileIndex={fileIndex}
                                                                    chatId={chat._id}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center justify-between mt-1">
                                                    {chat?.date && (
                                                        <div
                                                            className="text-[12px] text-gray-500 flex items-center gap-1 ml-3"
                                                            style={{ marginTop: '0px' }}>
                                                            <span className="ml-2 text-gray-500">
                                                                {new Date(chat?.date).toLocaleTimeString('en-US', {
                                                                    hour: '2-digit',
                                                                    minute: '2-digit',
                                                                })}
                                                            </span>
                                                            {" , "}
                                                            <span>
                                                                {new Date(chat?.date).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                })}
                                                            </span>

                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {!isMobile && (
                                                <div className="!w-[35px] flex-shrink-0">
                                                    <img
                                                        src={Avatar}
                                                        alt="Dabur Logo"
                                                        className="w-[40px]"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    ((selectedChat?._id &&
                                        selectedChat?._id === chat.sessionId) ||
                                        !selectedChat?._id) && (
                                        <div
                                            key={chat._id}
                                            className="mt-8 mb-8 max-w-4xl space-y-8">
                                            <div className="flex gap-4 w-[280px] sm:w-full">
                                                {!isMobile && (
                                                    <div className="w-[40px]">
                                                        <img
                                                            src={daburLogo}
                                                            alt="Dabur Logo"
                                                            className="w-[40px]"
                                                        />
                                                    </div>
                                                )}
                                                <div className="text-[#000000] flex flex-col gap-2 overflow-x-auto rounded-bl-lg rounded-br-lg rounded-tr-lg min-h-[44px] justify-center w-fit group">
                                                    {chat.type === 'loading' ? (
                                                        // <div className="p-4 border-0  !border-[#1B5C29] rounded-xl bg-white">
                                                        //     <p className="mb-2 text-base font-medium text-gray-600">
                                                        //         <RotatingTextLoader messages={[
                                                                    // "Processing audio file",
                                                                    // "Transcribing speech",
                                                                    // "Analyzing meeting context",
                                                                    // "Extracting action items",
                                                                    // "Formatting transcript"
                                                        //         ]} />
                                                        //     </p>
                                                        //     <DaburProgressBar progress={chat.progress} />
                                                        // </div>
                                                        <span className="p-2">
                                                            <RotatingTextLoader messages={[
                                                                    "Processing audio file",
                                                                    "Transcribing speech",
                                                                    "Analyzing meeting context",
                                                                    "Extracting action items",
                                                                    "Formatting transcript"
                                                            ]} />
                                                        </span>
                                                    ) : (
                                                        <div className="flex flex-col">
                                                            <div className="markDownWrapper !leadings-[27px] font-hel w-fit whitespace-pre-line break-words font-medium text-md">
                                                                {chat?.latest_message &&
                                                                    i === chats.length - 1 ? (
                                                                    <BotMarkdown
                                                                        content={preprocessText(
                                                                            chat?.isAudio
                                                                                ? chat?.message
                                                                                : displayedText,
                                                                        )}
                                                                    />
                                                                ) : Array.isArray(chat.message) ||
                                                                    typeof chat.message === 'string' ? (
                                                                    <BotMarkdown
                                                                        content={preprocessText(chat.message)}
                                                                    />
                                                                ) : (
                                                                    Object.keys(chat.message).map((locationKey, idx, keys) => {
                                                                        const firstKey = keys[0];
                                                                        const stored = expandedZipcodes[chat._id] && expandedZipcodes[chat._id][locationKey];
                                                                        const isOpen = stored !== undefined ? stored : locationKey === firstKey;
                                                                        return (
                                                                            <div key={locationKey} className="mb-6">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => toggleZipcode(chat._id, locationKey, locationKey === firstKey)}
                                                                                    className="w-full flex items-center justify-between font-bold text-lg mb-2 text-left">
                                                                                    <span>{locationKey}</span>
                                                                                    <span className="ml-2">
                                                                                        {isOpen ? (
                                                                                            <ChevronUp className="w-5 h-5" />
                                                                                        ) : (
                                                                                            <ChevronDown className="w-5 h-5" />
                                                                                        )}
                                                                                    </span>
                                                                                </button>
                                                                                {isOpen && (
                                                                                    <BotMarkdown
                                                                                        content={preprocessText(
                                                                                            chat.message[locationKey],
                                                                                        )}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })
                                                                )}
                                                                {chat?.summary?.length > 0 && (
                                                                    <div className="mb-4  ">
                                                                        <h3 className="font-bold mb-2">Summary</h3>
                                                                        <BotMarkdown
                                                                            content={preprocessText(
                                                                                chat?.summary?.map(point => `• ${point}`).join("\n\n")
                                                                            )}
                                                                        />
                                                                    </div>
                                                                )}

                                                            </div>
                                                            <div className="flex items-center justify-between mt-1">
                                                                <CopyToClipboard
                                                                    message={
                                                                        typeof chat.message === "string"
                                                                            ? chat.message
                                                                            : Array.isArray(chat.message)
                                                                                ? chat.message.join("\n")
                                                                                : Object.entries(chat.message)
                                                                                    .map(([key, value]) => `${key}: ${value}`)
                                                                                    .join("\n") // format object without brackets
                                                                    }
                                                                    id={chat._id}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                ),
                            )}
                        </InfiniteScroll>
                    </div>
                    <div
                        className={classNames(
                            '  bottom-0 mb-0 py-4 !px-[30px] sm:px-[50px] md:px-[20px] lg:px-[100px] xl:!px-[220px] 2xl:!px-[220px] ',
                            {
                                'opacity-50 pointer-events-none':
                                    loading && selectedChat?._id === tempId,
                            },
                        )}>
                        <div className=" flex flex-row gap-[10px] items-center justify-end">

                            <button
                                onClick={() => {
                                    setFiles([])
                                    setIsImportFileOpen(true)
                                }}
                                className={`text-[16px] bg-[#1B5C29] text-white leading-[24px] font-medium font-inter py-[10px] px-[25px] rounded-[8px]`}>
                                Upload Files
                            </button>
                        </div>
                    </div>
                </div>
            ) : selectedChat?._id ? (
                <div className="flex h-full items-center justify-center">
                    <SkeletonLoader />
                </div>
            ) : (
                <></>
            )}
        </div>
    );
}

MeetingTranscriptChatInterface.propTypes = {
    isMobile: PropTypes.bool,
    sidebarOpen: PropTypes.bool,
    setChats: PropTypes.func,
    chats: PropTypes.array,
};
